import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, CircleDashed, Loader2, Receipt, XCircle } from "lucide-react";

import { TipReceiptDetails } from "@/components/tip-receipt-details";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { formatMoney } from "@/lib/money";
import { getTipStatus } from "@/lib/tips.functions";

function formatStamp(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

/**
 * Shown when Stripe returns the reader to the site after checkout. It polls
 * until the signed webhook has settled the tip, so the reader sees the real
 * outcome rather than an optimistic "thank you".
 */
export function TipReceipt() {
  const { user } = useAuth();
  const [tipId, setTipId] = React.useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const id = new URLSearchParams(window.location.search).get("tip");
    setTipId(id);
  }, []);

  const load = useServerFn(getTipStatus);
  const { data, error } = useQuery({
    queryKey: ["tip-receipt", tipId],
    queryFn: () => load({ data: { tipId: tipId! } }),
    enabled: Boolean(tipId && user),
    retry: false,
    // Stripe's confirmation can lag the redirect by a few seconds.
    refetchInterval: (query) => (query.state.data?.settled ? false : 3000),
  });

  if (!tipId || !user) return null;

  function dismiss() {
    setTipId(null);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("tip");
      window.history.replaceState({}, "", url.toString());
    }
  }

  const status = data?.status ?? "pending";
  const steps: { label: string; stamp: string | null; state: "done" | "pending" | "failed" }[] = [
    {
      label: "Checkout started",
      stamp: formatStamp(data?.createdAt),
      state: "done",
    },
    {
      label: "Payment confirmed by Stripe",
      stamp: formatStamp(data?.completedAt),
      state:
        status === "completed" || status === "refunded" || status === "disputed"
          ? "done"
          : status === "failed"
            ? "failed"
            : "pending",
    },
    ...(status === "refunded" || status === "disputed"
      ? [
          {
            label: status === "refunded" ? "Refunded to your card" : "Disputed with your bank",
            stamp: formatStamp(data?.refundedAt),
            state: "done" as const,
          },
        ]
      : []),
  ];

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-border/60 bg-surface/95 p-4 shadow-lg backdrop-blur">
      <div className="flex items-start gap-3">
        <Receipt className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg text-foreground">Tip receipt</p>
          {error ? (
            <p className="mt-1 text-sm text-muted-foreground">
              We couldn&apos;t load this receipt. Your payment status is unaffected.
            </p>
          ) : !data ? (
            <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm text-muted-foreground">
                {formatMoney(data.amountMinor, data.currency)}
                {data.writerName ? ` to ${data.writerName}` : ""} —{" "}
                <span
                  className={
                    status === "completed"
                      ? "text-foreground"
                      : status === "pending"
                        ? "text-muted-foreground"
                        : "text-destructive"
                  }
                >
                  {status}
                </span>
              </p>

              <ol className="mt-3 space-y-2">
                {steps.map((step) => (
                  <li key={step.label} className="flex items-start gap-2 text-sm">
                    {step.state === "done" ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" aria-hidden="true" />
                    ) : step.state === "failed" ? (
                      <XCircle className="mt-0.5 h-4 w-4 text-destructive" aria-hidden="true" />
                    ) : (
                      <CircleDashed
                        className="mt-0.5 h-4 w-4 animate-pulse text-muted-foreground"
                        aria-hidden="true"
                      />
                    )}
                    <span className="min-w-0">
                      <span className="block text-foreground">{step.label}</span>
                      {step.stamp ? (
                        <span className="block text-xs text-muted-foreground">{step.stamp}</span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ol>

              {data.reference ? (
                <p className="mt-3 break-all text-xs text-muted-foreground">
                  Stripe reference: <span className="font-mono">{data.reference}</span>
                </p>
              ) : null}
            </>
          )}

          <div className="mt-3 flex justify-end gap-2">
            {data ? (
              <Button size="sm" variant="ghost" onClick={() => setDetailsOpen(true)}>
                View details
              </Button>
            ) : null}
            <Button size="sm" variant="soft" onClick={dismiss}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {data ? (
        <TipReceiptDetails open={detailsOpen} onOpenChange={setDetailsOpen} tip={data} />
      ) : null}
    </div>
  );
}
